import React, { useEffect, useMemo, useState } from "react";
import "./PdfDocumentSection.css";
import { toPdfPagePath } from "../utils/pdfRouting";
import {
  DEFAULT_PDF_STYLE_CONFIG,
  normalizePdfStyleConfig,
} from "../utils/pdfDocuments";

const asArray = (value) => (Array.isArray(value) ? value : []);

const trimTo = (value, max = 165) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}...`;
};

const byOrder = (a, b) => {
  const orderA = Number(a.sectionOrder || 0);
  const orderB = Number(b.sectionOrder || 0);
  if (orderA !== orderB) return orderA - orderB;
  return String(a.title || "").localeCompare(String(b.title || ""));
};

const toPx = (value, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${fallback}px`;
  return `${n}px`;
};

const buildPdfThumbnailUrl = (pdfUrl) => {
  const raw = String(pdfUrl || "").trim();
  if (!raw) return "";
  const [base, hashPart = ""] = raw.split("#");
  const params = new URLSearchParams(hashPart);
  if (!params.get("page")) params.set("page", "1");
  if (!params.get("toolbar")) params.set("toolbar", "0");
  if (!params.get("navpanes")) params.set("navpanes", "0");
  if (!params.get("scrollbar")) params.set("scrollbar", "0");
  if (!params.get("view")) params.set("view", "FitH");
  return `${base}#${params.toString()}`;
};

export default function PdfDocumentSection({
  documents = [],
  title = "Documents",
  subtitle = "",
  mode = "section",
  className = "",
  styleConfig = DEFAULT_PDF_STYLE_CONFIG,
}) {
  const availableDocuments = useMemo(
    () =>
      asArray(documents)
        .filter((doc) => doc?.enabled && doc?.pdfUrl)
        .sort(byOrder),
    [documents]
  );

  const [activeId, setActiveId] = useState(availableDocuments[0]?.id || "");

  useEffect(() => {
    if (!availableDocuments.length) {
      setActiveId("");
      return;
    }

    const stillExists = availableDocuments.some((doc) => doc.id === activeId);
    if (!stillExists) {
      setActiveId(availableDocuments[0].id);
    }
  }, [availableDocuments, activeId]);

  const activeDocument = useMemo(
    () =>
      availableDocuments.find((doc) => doc.id === activeId) || availableDocuments[0] || null,
    [availableDocuments, activeId]
  );
  const normalizedStyleConfig = useMemo(
    () => normalizePdfStyleConfig(styleConfig),
    [styleConfig]
  );
  const showInlineViewer = mode !== "page";
  const showSectionHeader = mode !== "page";

  if (!availableDocuments.length) return null;

  const rootClassName = ["pdfdoc-section", `mode-${mode}`, className]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={rootClassName}>
      {showSectionHeader && (
        <header className="pdfdoc-header">
          <p className="pdfdoc-kicker">PDF Library</p>
          <h2
            className="pdfdoc-title"
            style={{
              color: normalizedStyleConfig.sectionTitleColor,
              fontSize: toPx(
                normalizedStyleConfig.sectionTitleSize,
                DEFAULT_PDF_STYLE_CONFIG.sectionTitleSize
              ),
            }}
          >
            {title || "Documents"}
          </h2>
          {subtitle ? (
            <p
              className="pdfdoc-description"
              style={{
                color: normalizedStyleConfig.sectionSubtitleColor,
                fontSize: toPx(
                  normalizedStyleConfig.sectionSubtitleSize,
                  DEFAULT_PDF_STYLE_CONFIG.sectionSubtitleSize
                ),
              }}
            >
              {subtitle}
            </p>
          ) : null}
        </header>
      )}

      <div className="pdfdoc-grid">
        {availableDocuments.map((doc, idx) => {
          const isActive = showInlineViewer && doc.id === activeDocument?.id;
          const cardTitleColor = normalizedStyleConfig.applyToAllCards
            ? normalizedStyleConfig.cardTitleColor
            : doc.cardTitleColor || normalizedStyleConfig.cardTitleColor;
          const cardDescriptionColor = normalizedStyleConfig.applyToAllCards
            ? normalizedStyleConfig.cardDescriptionColor
            : doc.cardDescriptionColor || normalizedStyleConfig.cardDescriptionColor;
          const cardTitleSize = normalizedStyleConfig.applyToAllCards
            ? normalizedStyleConfig.cardTitleSize
            : doc.cardTitleSize;
          const cardDescriptionSize = normalizedStyleConfig.applyToAllCards
            ? normalizedStyleConfig.cardDescriptionSize
            : doc.cardDescriptionSize;
          const cardTitle = doc.title || `PDF Document ${idx + 1}`;
          const cardDescription =
            trimTo(doc.description, showInlineViewer ? 165 : 96) ||
            (showInlineViewer
              ? "Click to open this PDF and review it in full view."
              : "Open this PDF document.");
          const placementLabel =
            doc.placementType === "page"
              ? toPdfPagePath(doc.pageSlug)
              : `${doc.sectionPage} • order ${Number(doc.sectionOrder || idx + 1)}`;

          if (!showInlineViewer) {
            return (
              <a
                key={doc.id}
                href={doc.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pdfdoc-card pdfdoc-gallery-card"
                aria-label={`Open PDF: ${cardTitle}`}
              >
                <div className="pdfdoc-gallery-media">
                  <iframe
                    title={`${cardTitle} preview`}
                    src={buildPdfThumbnailUrl(doc.pdfUrl)}
                    className="pdfdoc-gallery-preview"
                    loading="lazy"
                    tabIndex={-1}
                  />
                  <div className="pdfdoc-gallery-overlay" aria-hidden="true" />
                  <span className="pdfdoc-gallery-chip">PDF</span>
                  <span className="pdfdoc-gallery-icon" aria-hidden="true">
                    ↗
                  </span>
                </div>
                <div className="pdfdoc-gallery-body">
                  <h3
                    className="pdfdoc-card-title"
                    style={{
                      color: cardTitleColor,
                      fontSize: toPx(
                        cardTitleSize,
                        DEFAULT_PDF_STYLE_CONFIG.cardTitleSize
                      ),
                    }}
                  >
                    {cardTitle}
                  </h3>
                  <p
                    className="pdfdoc-card-text"
                    style={{
                      color: cardDescriptionColor,
                      fontSize: toPx(
                        cardDescriptionSize,
                        DEFAULT_PDF_STYLE_CONFIG.cardDescriptionSize
                      ),
                    }}
                  >
                    {cardDescription}
                  </p>
                </div>
              </a>
            );
          }

          return (
            <button
              key={doc.id}
              type="button"
              className={`pdfdoc-card ${isActive ? "is-active" : ""}`}
              onClick={() => setActiveId(doc.id)}
              aria-label={`Open preview: ${cardTitle}`}
            >
              <div className="pdfdoc-card-badge">{placementLabel}</div>
              <h3
                className="pdfdoc-card-title"
                style={{
                  color: cardTitleColor,
                  fontSize: toPx(
                    cardTitleSize,
                    DEFAULT_PDF_STYLE_CONFIG.cardTitleSize
                  ),
                }}
              >
                {cardTitle}
              </h3>
              <p
                className="pdfdoc-card-text"
                style={{
                  color: cardDescriptionColor,
                  fontSize: toPx(
                    cardDescriptionSize,
                    DEFAULT_PDF_STYLE_CONFIG.cardDescriptionSize
                  ),
                }}
              >
                {cardDescription}
              </p>
              <div className="pdfdoc-card-footer">
                <span>{showInlineViewer ? "Open Preview" : "Open PDF"}</span>
                <span aria-hidden="true">↗</span>
              </div>
            </button>
          );
        })}
      </div>

      {showInlineViewer && activeDocument && (
        <div className="pdfdoc-viewer-shell">
          <div className="pdfdoc-viewer-head">
            <div>
              <h3
                className="pdfdoc-viewer-title"
                style={{
                  color: normalizedStyleConfig.viewerTitleColor,
                  fontSize: toPx(
                    normalizedStyleConfig.viewerTitleSize,
                    DEFAULT_PDF_STYLE_CONFIG.viewerTitleSize
                  ),
                }}
              >
                {activeDocument.title}
              </h3>
              {activeDocument.description ? (
                <p
                  className="pdfdoc-viewer-desc"
                  style={{
                    color: normalizedStyleConfig.viewerDescriptionColor,
                    fontSize: toPx(
                      normalizedStyleConfig.viewerDescriptionSize,
                      DEFAULT_PDF_STYLE_CONFIG.viewerDescriptionSize
                    ),
                  }}
                >
                  {activeDocument.description}
                </p>
              ) : null}
            </div>
            <div className="pdfdoc-actions">
              <a
                href={activeDocument.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pdfdoc-btn primary"
              >
                View PDF
              </a>
              <a href={activeDocument.pdfUrl} download className="pdfdoc-btn">
                {activeDocument.buttonText || "Download PDF"}
              </a>
            </div>
          </div>

          <div className="pdfdoc-iframe-shell">
            <iframe
              title={activeDocument.title || "PDF preview"}
              src={activeDocument.pdfUrl}
              className="pdfdoc-viewer"
              style={{ height: `${activeDocument.viewerHeight || 700}px` }}
              loading="lazy"
            />
          </div>

          <p className="pdfdoc-note">
            If preview is blocked by browser policy, use View PDF or Download PDF.
          </p>
        </div>
      )}
    </section>
  );
}
